/**
 * ErrorsToast.js
 *
 * Redux -> Motif Toast bridge that replaces the legacy `Errors` +
 * `react-notification-system` global notification surface. Subscribes to the
 * `store.errors` slice (fed by API_ERRORS, CLIENT_VALIDATION_ERROR,
 * SUCCESS_MESSAGE, WARNING_MESSAGE, INFO_MESSAGE), formats the payload with
 * the same rules the legacy component used (error-code expansion, network
 * error mapping, minor error suppression), forwards it to the global toast
 * via `notify.<variant>`, and clears the slice so it stays empty.
 *
 * Renders nothing. Mount once, next to the global `<Toast area={global}/>`.
 */
import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {isEmpty} from 'lodash';
import {clearErrors} from '../../errors';
import {labels, currentResource} from '../../utils/utils';
import {minorErrorCodes} from '../../utils/uiconstants';
import {notify} from './notify';

const getErrorCodeValue = (errorCodes) => {
	if (errorCodes.length === 0) {
		return errorCodes;
	}
	return errorCodes.map((code) => `${code}: ${currentResource.errors[code] ?? ''}`);
};

const formatErrorMessage = (errors) => {
	if (isEmpty(errors)) {
		return '';
	}

	const errorCodes = [];
	let errorMessages = [];
	let error;
	let message = '';
	let errorMessage = '';

	try {
		if (typeof errors === 'string' && errors.indexOf(' - ') > 0) {
			error = errors.substr(0, errors.indexOf(' -'));
		} else if (typeof errors === 'string') {
			error = JSON.parse(errors);
		} else {
			error = errors;
		}
	} catch (err) {
		message = errors;
	}

	if (error) {
		if (Array.isArray(error) && error.length > 0) {
			error.forEach((e) => {
				if (e.ErrorCode) {
					errorCodes.push(e.ErrorCode);
				} else if (e.Message) {
					errorMessages.push(e.Message);
				}
			});
		} else if (error.ErrorCode) {
			errorCodes.push(error.ErrorCode);
		} else if (typeof error === 'number' || (typeof error === 'string' && !Number.isNaN(Number(error)))) {
			errorCodes.push(Number(error));
		}

		if (errorCodes.length > 0) {
			errorMessages = getErrorCodeValue(errorCodes);
		}

		if (errorMessages.length > 0) {
			errorMessages.forEach((m) => {
				errorMessage += `${m}\n`;
			});
			return errorMessage.trim();
		}
	}

	return message;
};

const isMinorError = (errorPayload) => {
	if (isEmpty(errorPayload)) {
		return false;
	}
	let candidate;
	if (typeof errorPayload === 'string') {
		candidate = errorPayload;
	} else if (typeof errorPayload === 'object' && typeof errorPayload.message === 'string') {
		candidate = errorPayload.message;
	}
	if (!candidate) {
		return false;
	}
	const errorCode = candidate.substr(0, 4);
	return minorErrorCodes.some((minor) => parseInt(errorCode, 10) === minor);
};

const resolveLevel = (errors) => {
	if (errors && typeof errors === 'object' && errors.level) {
		return errors.level;
	}
	return 'error';
};

export function ErrorsToast() {
	const errors = useSelector((store) => store.errors);
	const isModalOpen = useSelector((store) => store.isModalOpen);
	const dispatch = useDispatch();

	useEffect(() => {
		if (isModalOpen) {
			return;
		}

		if (errors === undefined || errors === null) {
			return;
		}

		if (Array.isArray(errors)) {
			if (errors.length === 0 || isMinorError(errors)) {
				return;
			}
		} else if (typeof errors === 'object') {
			if (Object.keys(errors).length === 0 || isMinorError(errors)) {
				return;
			}
		} else if (typeof errors === 'string') {
			if (errors.length === 0 || isMinorError(errors)) {
				return;
			}
		} else {
			return;
		}

		const level = resolveLevel(errors);
		let message = errors && errors.message ? errors.message : formatErrorMessage(errors);

		if (message === 'Network Error') {
			message = labels.NetworkErrorMessage;
		}

		if (isEmpty(message)) {
			return;
		}

		const raise = notify[level] ?? notify.error;
		raise(message);

		dispatch(clearErrors());
	}, [errors, isModalOpen, dispatch]);

	return null;
}

// Exposed only so tests can exercise the same formatting used by the effect.
export const __internals = {formatErrorMessage, isMinorError, resolveLevel};

export default ErrorsToast;
